import { useMap } from "@vis.gl/react-google-maps";
import { Button, Input, InputRef, Switch } from "antd";
import React, { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useTrackEvent } from "../../../../components/AnalyticsProvider/useTrackEvent";
import Icon, { ICON_NAME } from "../../../../components/Icon";
import { RouteService } from "../../../../util/services/route";
import { AppState } from "../../../../util/store/store";
import { showErrorResponse } from "../../../../util/varibles/global";
import { IPoint, IRoute } from "../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../util/varibles/message";
import { planOpActions } from "../../../PlanOperationPage/reducer";
import { RouteMode } from "../../constatns";
import { routeActions } from "../../reducer";
import AutoRoute from "./AutoRoute";
import styles from './style.module.scss';


const mapStateToProps = (state: AppState) => ({
    params: state.route.params,
    route: state.route.route,
    routes: state.route.routes,
    loadingAuto: state.route.loadingAuto,
    mode: state.route.mode,
    autoRouteLicense: state.login.user.tenant.auto_route_license
});

interface IProps {
    params: any
    routes: IRoute[]
    loadingAuto: boolean
    autoRouteLicense?: boolean
    mode: RouteMode
    route: any

    changeMode(payload: { mode: RouteMode, route: any }): void
}

const PopupEditRoute: React.FC<IProps> = (props) => {
    const { route, routes, mode } = props;
    const { id, name = '' } = route || {};
    const { source, destination } = props.params;
    const [loading, setLoading] = useState(false);
    const map = useMap('map');
    const inputRef = React.useRef<InputRef>(null);
    const dispatch = useDispatch();
    const trackEvent = useTrackEvent();

    useEffect(() => {
        if (mode === RouteMode.EDIT) {
            inputRef.current?.focus();
        }
    }, [mode])

    const handleSubmit = () => {
        const { points = [], id = null, name = '', } = route || {};
        if (points.length <= 2)
            return;
        const params = {
            router_id: id,
            source_id: source.id,
            destination_id: destination.id,
            default: route.default,
            type: [source.field === 'factory' ? 'factory' : 'site', destination.field === 'factory' ? 'factory' : 'site'].join('-'),
            name,
            points: points.map((item: any, index: number) => ({
                point_id: index,
                name: item.name || `Way point ${index}`,
                latitude: item.latitude,
                longitude: item.longitude,
                depth: 0,
            }))
        };
        let fetch: (params: any) => Promise<IRoute>, action: any, title: any, notifyCode: any;
        if (id === '-') {
            notifyCode = NotifyCode.S1;
            fetch = RouteService.create;
            action = routeActions.addRoute
            title = 'Create route';
        } else {
            notifyCode = NotifyCode.S2;
            fetch = RouteService.update;
            action = routeActions.updateRoute
            title = 'Update route';
        }

        trackEvent('route_planned', 'Save Route', {
            route_mode: route.route_mode || 'manual',
            source_id: source.id,
            source_name: source.name,
            destination_id: destination.id,
            destination_name: destination.name,
        });
        setLoading(true);
        fetch(params)
            .then(rs => {
                const data = { ...params, ...rs };
                dispatch(action(data))
                dispatch(planOpActions.adddRoute(data));
                notify.success(notifyCode)([data.name]);
                setLoading(false);
            })
            .catch(error => {
                showErrorResponse(title + ' failed', error)
                setLoading(false);
            })
    };

    const handleDeleteMarker = (e: React.MouseEvent, value: number) => {
        e.preventDefault();
        e.stopPropagation();
        const position = value + 1;
        dispatch(routeActions.editRoute({
            ...route,
            points: route.points.reduce((list: any, item: any, index: number) => {
                if (position === index)
                    return list;
                return [...list, item]
            }, [])
        }))
    };

    const handleUpdate = (attr: any) => {
        dispatch(routeActions.editRoute({ ...route, ...attr }))
    }

    const handleCancel = () => {
        const data = routes.find(item => item.id === id) || routes[0] || {};
        props.changeMode({ mode: RouteMode.VIEW, route: data })
    }

    const handleClick = useCallback((point: IPoint) => {
        if (!map) return;

        const latitude = Number(point.latitude);
        const longitude = Number(point.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

        map.panTo({ lat: latitude, lng: longitude });
        map.setZoom(11);
    }, [map]);


    if (!route || mode === RouteMode.VIEW) return null;

    let points = Array.isArray(route?.points) ? [...route.points] : [];
    points = points.slice(1);
    points.pop();

    return <div className={styles['container-edit']}>
        <div className="flex mt-1 items-center gap-1 flex-wrap">
            <span className="text-[18px] whitespace-nowrap">{id === "-" ? "Create route" : "Update route"}</span>
            <div className="flex items-center gap-1">
                <Icon className="mx-1 mt-[2px]" icon={ICON_NAME.ARROW_RIGHT_3} />
                <span>{source.name}</span>
                <span className='arrow-right' />
                <span>{destination.name}</span>
            </div>
        </div>
        <div className={styles.info}>
            <Input
                placeholder="Name"
                ref={inputRef}
                className='h-36 border-r-40'
                value={name}
                onChange={(e: any) => handleUpdate({ name: e.target.value })}
            />
        </div>
        <div className="flex justify-end gap-1 items-center mt-2">
            <span>Default</span>
            <Switch
                checked={route.default}
                onChange={checked => handleUpdate({ default: checked })}
            />
        </div>
        <div className={[styles.points, 'scroll-small'].join(' ')}>
            {points.length === 0
                ? <div className={styles.empty} />
                : <>
                    <div className={styles.source} data-point-field={source.field}>{source.name}</div>
                    {points.map((item, index: number) => <div
                        key={"position" + index}
                        id={`position_${index}`}
                        className={styles.point}
                        onClick={() => handleClick(item)}
                    >
                        <div className={styles.pointName}>
                            <span>{index + 1}</span>
                            Way point {index + 1}
                        </div>
                        <Button className='bt-ghost absolute top-[4px] right-[4px]' data-icon={true} data-danger={true}
                            onClick={(e) => handleDeleteMarker(e, index)}>
                            <Icon icon={ICON_NAME.DELETE} />
                        </Button>
                    </div>)}
                    <div className={styles.source} data-point-field={destination.field}>{destination.name}</div>
                </>}
        </div>
        <div className={styles.description}>
            Right click to create route manually or <AutoRoute source={source} destination={destination} />
        </div>
        <div className="flex justify-end gap-2 mt-2 mb-1">
            <div className="bt-default" onClick={handleCancel}>
                <span>Cancel</span>
            </div>
            <Button className="bt-primary" type='primary'
                disabled={points.length <= 2}
                loading={loading}
                onClick={handleSubmit}
            >
                Save route
            </Button>
        </div>
    </div>;
};

export default connect(mapStateToProps, {
    changeMode: routeActions.changeMode,
})(PopupEditRoute);
